import dayjs from "dayjs";
import { File, User, MapPin, Calendar, FileText, CalendarCheck2 } from "lucide-react";
import { CONFIG } from "../config";
import { getPermissionKey } from "./permissions";
import GenericStatusComponent from "../genericComponents/GenericStatusComponent";
import { getCurrentDateFormat, modifyDataForDocumentViewerUrlInsertion } from "./helperTableFunctions";
import Icons from "../assets/icons";
import LanguageTranslator from "../locals/LanguageTranslator";
import { getPayloadFromURLParams, setPayloadAsURLParams } from "./URLParamsHelper";

const commonProps = {
    permissionsObj: { view: getPermissionKey("common", "commonFunctionality", "view") },
    getPayload: (config = {}) => {
        let { type, documentInfo, data = {} } = config;
        switch (type) {
            case "header": {
                return {
                    "documentType": documentInfo?.documentType || "",
                    "documentNo": documentInfo?.documentNo || "",
                    "apiUrl": "/admin/document/get/data",
                }
            }
            case "setLevel": {
                return {
                    "documentType": documentInfo?.documentType || "",
                    "documentNo": documentInfo?.documentNo || "",
                    "apiUrl": "/admin/document/get/data",
                    "detailType": "ITEM",
                    "baseUrl": CONFIG.SM_BACKEND_BASE_URL,
                }
            }
            default: {
                return {
                    "documentType": documentInfo?.documentType || "",
                    "documentNo": documentInfo?.documentNo || "",
                    "apiUrl": "/admin/document/get/data",
                    "detailType": type,
                    "baseUrl": CONFIG.SM_BACKEND_BASE_URL
                };
            }
        }
    },
    customCols: {

    }
};

export const PO_DOCUMENT_CONFIG = (props) => {
    const { uType, tab, tableHeadersMap, handleChange } = props;

    return {
        actionBarConfig: {
            downloadDocuments: tab === "SHIPMENT_CANCELLED" ? false : {
                disabled: false,
                page: "order",
                disableItems: uType == "VENDOR" && tab == "PENDING_APPROVAL" ? (config) => {
                    const { selectedItems = {}, currentItem = {} } = config || {};

                    switch (currentItem?.key) {
                        case "ITEM_BARCODE":
                        case "SET_PRINTER_BARCODE":
                        case "ITEM_PRINTER_BARCODE": {
                            return true;
                        }
                        default: {
                            return false;
                        }
                    }
                } : () => false
            },
            downloadReport: {},
            quickFiltersPayload: {
                attributeType: tableHeadersMap?.[tab]?.main?.key,
                displayName: tableHeadersMap?.[tab]?.main?.key,
                filterName: ""
            }
        },
        isMultipleLevelAvailable: (item) => item?.is_set == "true",
        permissionsObj: commonProps?.permissionsObj,
        getLabel: (level, data) => {
            switch (level) {
                case 0: return "Order Details";
                default: return "";
            }
        },
        addKeysWithCurrentKeysData: {
            orderNumber: "order_no",
            id: "document_id"
        },
        // To get the payload for the get information api's
        getPayload: commonProps.getPayload,
        customCols: {
            ITEM: {
                icode: (item) => <div className='py-2.5 flex flex-col gap-0.5'>
                    <label className='mb-0 !text-xs !font-semibold !text-[#111D31]'>{item?.icode}</label>
                    <span className='text-[10px] text-[#6A6C70]'>{item?.hl4_name}</span>
                </div>,
                hl4_name: (item) => <div className='py-2.5 flex flex-col gap-0.5'>
                    <label className='mb-0 !text-xs !font-semibold !text-[#111D31]'>{item?.hl4_name}</label>
                    <span className='text-[10px] text-[#6A6C70]'>HSN: {item?.hsn || "-"}</span>
                </div>,
                design_no: (item) => <div className='py-2.5 flex flex-col gap-0.5'>
                    <label className='mb-0 !text-xs !font-semibold !text-[#111D31]'>{item?.design_no || "-"}</label>
                    <span className='text-[10px] text-[#6A6C70]'>{item?.color || "-"}</span>
                </div>,
                order_qty: (item) => <div className='py-2.5 flex flex-col gap-0.5'>
                    <label className='mb-0 !text-xs !font-semibold !text-[#111D31]'>{item?.order_qty}</label>
                    <span className='text-[10px] text-[#6A6C70]'>Ratio: {item?.nbset || "-"}</span>
                </div>,
                t_cancelled_qty: (item) => <label className='text-xs font-medium !text-[#EE5A24]'>{item?.t_cancelled_qty}</label>,
                grc_qty: (item) => <label className='text-xs font-medium text-[#00891A]'>{item?.grc_qty}</label>,
            },
            ASN: {
                shipment_advice_code: (item) => <div className='py-2.5 flex flex-col'>
                    <label className='mb-0 !text-xs !font-semibold !text-[#111D31] !cursor-pointer' onClick={() => handleChange("openAsnDocViewer", item)}>{item?.shipment_advice_code}</label>
                    <GenericStatusComponent size='small' className={'mb-0'} status={item?.shipment_status} />
                </div>,
                t_requested_qty: (item) => <div className='py-2.5 flex flex-col gap-0.5'>
                    <label className='mb-0 !text-[11px] !font-medium !text-[#111D31]'>{item?.t_requested_qty}</label>
                    <label className='text-[10px] text-[#6A6C70]'>Boxes: {item?.box_count}</label>
                </div>,
                t_cancelled_qty: (item) => <label className='text-xs font-medium !text-[#EE5A24]'>{item?.t_cancelled_qty}</label>,
                invoice_number: (item) => <div className='py-2.5 flex flex-col gap-0.5'>
                    <label className='mb-0 !text-xs !font-semibold !text-[#111D31]'>{item?.invoice_number}</label>
                    <span className='text-[10px] text-[#6A6C70]'>{item?.invoice_date ? dayjs(item?.invoice_date, getCurrentDateFormat(item?.invoice_date))?.format("DD MMM YYYY") : ""}</span>
                </div>,
                inspection_number: (item) => <div className='py-2.5 flex flex-col gap-0.5'>
                    <label className='mb-0 !text-xs !font-semibold !text-[#111D31] flex items-center gap-1.5'>{item?.inspection_number ?? "NA"} <GenericStatusComponent size='small' className={'mb-0'} status={item?.qc_status} /></label>
                    <span className='text-[10px] text-[#6A6C70]'>{item?.qc_from_date ? dayjs(item?.qc_from_date, getCurrentDateFormat(item?.qc_from_date))?.format("DD MMM YYYY") : ""} - {item?.qc_to_date ? dayjs(item?.qc_to_date, getCurrentDateFormat(item?.qc_to_date))?.format("DD MMM YYYY") : ""}</span>
                </div>,
                shipment_date: (item) => <label className='text-[10px] text-[#6A6C70]'>{item?.shipment_date ? dayjs(item?.shipment_date, getCurrentDateFormat(item?.shipment_date))?.format("DD MMM YYYY") : ""}</label>,
            },
            LR: {
                logistic_no: (item) => <div className='py-2.5 flex flex-col'>
                    <label className='mb-0 !text-xs !font-semibold !text-[#111D31]'>{item?.logistic_no}</label>
                    <GenericStatusComponent size='small' className={'mb-0'} status={item?.lr_status} />
                </div>,
                shipment_advice_code: (item) => <label className='mb-0 !text-xs !font-semibold !text-[#111D31]'>{item?.shipment_advice_code}</label>,
                appointment_date: (item) => <label>{item?.appointment_date ? dayjs(item?.appointment_date, getCurrentDateFormat(item?.appointment_date))?.format("DD MMM YYYY") : ""}</label>,
                t_requested_qty: (item) => <div className='py-2.5 flex flex-col gap-0.5'>
                    <label className='mb-0 !text-xs !font-semibold !text-[#111D31]'>{item?.t_requested_qty}</label>
                    <span className='text-[10px] text-[#6A6C70]'>No. of Cartons: {item?.no_of_cartons}</span>
                </div>,
            },
            GRC: {
                grc_number: (item) => <div className='py-2.5 flex flex-col'>
                    <label className='mb-0 !text-xs !font-semibold !text-[#111D31]'>{item?.grc_number}</label>
                    <div className='flex items-center gap-1.5'>
                        <GenericStatusComponent size='small' className={'mb-0'} status={item?.grc_status} />
                        <span className='text-[10px] text-[#6A6C70]'>{item?.erp_grc_date ? dayjs(item?.erp_grc_date, getCurrentDateFormat(item?.erp_grc_date))?.format("DD MMM YYYY") : ""}</span>
                    </div>
                </div>,
                sm_grc_number: (item) => <div className='py-2.5 flex flex-col gap-0.5'>
                    <label className='mb-0 !text-xs !font-semibold !text-[#111D31]'>{item?.sm_grc_number}</label>
                    <span className='text-[10px] text-[#6A6C70]'>{item?.sm_grc_date ? dayjs(item?.sm_grc_date, getCurrentDateFormat(item?.sm_grc_date))?.format("DD MMM YYYY") : ""}</span>
                </div>,
                shipment_advice_code: (item) => <div className='py-2.5 flex flex-col gap-0.5'>
                    <label className='mb-0 !text-xs !font-semibold !text-[#111D31]'>{item?.shipment_advice_code}</label>
                    <span className='text-[10px] text-[#6A6C70]'>ASN Qty: {item?.shipment_qty}</span>
                </div>,
                rejected_qty: (item) => <label className='text-xs font-medium !text-[#EE5A24]'>{item?.rejected_qty}</label>,
                gate_entry_no: (item) => <label className='mb-0 !text-xs !font-semibold !text-[#111D31]'>{item?.gate_entry_no}</label>
            },
            PI: {
                indent_no: (item) => <div className='py-2.5 flex flex-col gap-0.5'>
                    <label className='mb-0 !text-xs !font-semibold !text-[#111D31]'>{item?.indent_no}</label>
                    <span className='text-[10px] text-[#6A6C70]'>Created: {item?.indent_date ? dayjs(item?.indent_date, getCurrentDateFormat(item?.indent_date))?.format("DD MMM YYYY") : ""}</span>
                </div>,
                status: (item) => <GenericStatusComponent size='small' className={'mb-0'} status={item?.status} />,
                t_cancelled_qty: (item) => <label className='text-xs font-medium !text-[#EE5A24]'>{item?.t_cancelled_qty}</label>,
            }
        },
        setAllQtyKeys: {
            set_order_qty: "Ordered: ",
            set_requested_qty: "Requested: ",
            set_cancelled_qty: "Cancelled: ",
            set_grc_qty: "GRC: ",
        },
        imageKey: "image_url",
        headerKeys: {
            updationtime: "Order Date",
            supplier_name: "Supplier Name",
            site_name: "Site Name",
            po_validity: "Validity",
            po_quantity: "PO Qty",
            po_amount: "PO Amount (Incl. Taxes)",
            order_no: "PO Number",
            typeofbuying: "Type Of Buying",
            grc_qty: "GRC Qty",
            asn_qty: "ASN Qty",
            canceled_qty: "Cancelled Qty",
            pending_qty: "Pending Qty",
            termname: "Term"
        },
        docTabData: {
            ITEM: "Item Details",
            ASN: "ASN",
            LR: "LR",
            GRC: "GRC",
            PI: "Indent",
            liveComments: "Comments"
        },
        docViewerHeaderKeys: uType == "ENT" ? {
            MAIN: {
                name: "Main",
                key: "ENT_DOCUMENT_VIEWER_PO"
            },
            ITEM: {
                name: "Item",
                key: "ENT_DOCUMENT_VIEWER_PO_ITEM"
            },
            ASN: {
                name: "ASN",
                key: "ENT_DOCUMENT_VIEWER_PO_ASN"
            },
            LR: {
                name: "LR",
                key: "ENT_DOCUMENT_VIEWER_PO_LR"
            },
            GRC: {
                name: "GRC",
                key: "ENT_DOCUMENT_VIEWER_PO_GRC"
            },
            PI: {
                name: "Indent",
                key: "ENT_DOCUMENT_VIEWER_PO_PI"
            },
        } : {
            MAIN: {
                name: "Main",
                key: "VENDOR_DOCUMENT_VIEWER_PO"
            },
            ITEM: {
                name: "Item",
                key: "VENDOR_DOCUMENT_VIEWER_PO_ITEM"
            },
            ASN: {
                name: "ASN",
                key: "VENDOR_DOCUMENT_VIEWER_PO_ASN"
            },
            LR: {
                name: "LR",
                key: "VENDOR_DOCUMENT_VIEWER_PO_LR"
            },
            GRC: {
                name: "GRC",
                key: "VENDOR_DOCUMENT_VIEWER_PO_GRC"
            },
            PI: {
                name: "Indent",
                key: "VENDOR_DOCUMENT_VIEWER_PO_PI"
            }
        },
        headerKeyIcons: {
            order_no: <File />,
            supplier_name: <User />,
            site_name: <MapPin />,
            po_validity: <Calendar />,
            typeofbuying: () => <Icons.TypeOfBuying height={14} />,
            termname: () => <Icons.Termname height={14}/>
        },
        docViewerComment: {
            disabled: false,
            configKeys: {
                "documentId": "document_id",
                "documentNumber": "order_no",
                "vendorCode": "supplier_code",
                "documentType": "documentType"
            }
        },
        headerConfig: {
            section1: {
                timeKey: "updationtime",
                documentIcon: <span><FileText strokeWidth={1.75} /></span>,
                statusKeys: ["is_set", "status", "view_remarks"],
                headerKey: "erp_order_no",
                remarksKey: "remark",
                allDetails: {},
            },
            section2: {
                detailsKeys: uType == "ENT" ? ["order_no", "supplier_name", "site_name", "po_validity", "typeofbuying"] : ["order_no", "site_name", "po_validity", "typeofbuying", "termname"]
            },
            section3: {
                exportToExcel: true,
                downloadDocuments: true,
            },
            section4: {
                totalQty: "po_quantity",
                amountKey: "po_amount",
                remainingQtyKey: ["grc_qty", "asn_qty", "canceled_qty", "pending_qty"],
            },
            section5: {
                fulfill_percentage: "Fulfillment",
            }
        },
        columnConfig: {
            "updationtime": (config = {}) => {
                let { data, } = config || {};
                return data?.["order_date"] ? dayjs(data?.["order_date"])?.format("MMM DD YYYY HH:mm") : "";
            },
            "is_set": (config = {}) => {
                let item = { ...config?.data, poType: config?.data?.is_set == "true" ? "adhoc" : "poicode", status: config?.data?.po_status }
                return (
                    <>
                        <div className="poStatus">
                            {item?.poType === "poicode" ? <span className="partialTable setTag nonSetTag labelEvent">N</span> : <span className="partialTable setTag labelEvent">S</span>}
                            {item?.status === "PARTIAL" ? <span className="partialTable partiaTag labelEvent">P</span> : null}
                            <div className="poStatusToolTip">
                                {item?.poType === "poicode" ? <div className="each-tag"><span className="partialTable setTag nonSetTag labelEvent">N</span><LanguageTranslator tag="label">Non-set</LanguageTranslator></div> : <div className="each-tag"><span className="partialTable setTag labelEvent">S</span><LanguageTranslator tag="label">Set</LanguageTranslator></div>}
                                {item?.status === "PARTIAL" ? <div className="each-tag"><span className="partialTable partiaTag labelEvent">P</span><LanguageTranslator tag="label">Partial</LanguageTranslator></div> : ""}
                            </div>
                        </div>
                    </>
                );
            },
            "status": (data) => data?.po_status != "PARTIAL" ? <GenericStatusComponent className={'mb-0'} status={data?.po_status} /> : null,
            "po_validity": (config = {}) => {
                let { data } = config || {};
                return `${data?.valid_from ? dayjs(data?.valid_from)?.format("DD MMM YYYY") : ""} - ${data?.valid_to ? dayjs(data?.valid_to)?.format("DD MMM YYYY") : ""}`;
            }
        },
    }

};

export const ASN_DOCUMENT_CONFIG = (props) => {
    const { uType, tab, tableHeadersMap } = props;
    return {
        actionBarConfig: {
            downloadDocuments: tab === "SHIPMENT_CANCELLED" ? false : {
                disabled: false,
                page: "shipment"
            },
            quickFiltersPayload: {
                attributeType: tableHeadersMap?.[tab]?.main?.key,
                displayName: tableHeadersMap?.[tab]?.main?.key,
                filterName: ""
            },
            downloadReport: {},
        },
        isMultipleLevelAvailable: (item) => item?.is_set == "true",
        permissionsObj: commonProps?.permissionsObj,
        modifyExpandFirstListData: (data, config) => {
            let { mainExpandedRow } = config;
            let id = mainExpandedRow?.is_set == "true" ? "set_barcode" : "icode";
            return data?.resource?.map(item => ({ ...item, id: item?.[id] }))
        },
        modifyExpandSecondListData: (data) => data?.resource || [],
        getLabel: (level, data) => {
            switch (level) {
                case 0: return "ASN Details";
                default: return "";
            }
        },
        // To get the payload for the get information api's
        getPayload: commonProps.getPayload,
        customCols: {
            ITEM: {
                icode: (item) => <div className='py-2.5 flex flex-col gap-0.5'>
                    <label className='mb-0 !text-xs !font-semibold !text-[#111D31]'>{item?.icode}</label>
                    <span className='text-[10px] text-[#6A6C70]'>{item?.hl4_name}</span>
                </div>,
                hl4_name: (item) => <div className='py-2.5 flex flex-col gap-0.5'>
                    <label className='mb-0 !text-xs !font-semibold !text-[#111D31]'>{item?.hl4_name}</label>
                    <span className='text-[10px] text-[#6A6C70]'>HSN: {item?.hsn || "-"}</span>
                </div>,
                design_no: (item) => <div className='py-2.5 flex flex-col gap-0.5'>
                    <label className='mb-0 !text-xs !font-semibold !text-[#111D31]'>{item?.design_no || "-"}</label>
                    <span className='text-[10px] text-[#6A6C70]'>{item?.color || "-"}</span>
                </div>,
                order_qty: (item) => <div className='py-2.5 flex flex-col gap-0.5'>
                    <label className='mb-0 !text-xs !font-semibold !text-[#111D31]'>{item?.order_qty}</label>
                    <span className='text-[10px] text-[#6A6C70]'>Ratio : {item?.nbset || "-"}</span>
                </div>,
                t_cancelled_qty: (item) => <label className='text-xs font-medium !text-[#EE5A24]'>{item?.t_cancelled_qty}</label>,
                grc_qty: (item) => <label className='text-xs font-medium text-[#00891A]'>{item?.grc_qty}</label>,
            },
            INSPECTION: {
                inspection_number: (item) => <div className='py-2.5 flex flex-col gap-0.5'>
                    <label className='mb-0 !text-xs !font-semibold !text-[#111D31] flex items-center gap-1.5'>{item?.inspection_number ?? ""} <GenericStatusComponent className={'mb-0'} status={item?.qc_status} /></label>
                    <span className='text-[10px] text-[#6A6C70]'>{item?.qc_from_date ? dayjs(item?.qc_from_date, getCurrentDateFormat(item?.qc_from_date))?.format("DD MMM YYYY") : ""} - {item?.qc_to_date ? dayjs(item?.qc_to_date, getCurrentDateFormat(item?.qc_to_date))?.format("DD MMM YYYY") : ""}</span>
                </div>,
                inspection_confirmed_by: (item) => <div className='py-2.5 flex flex-col gap-0.5'>
                    <label className='mb-0 !text-xs !font-semibold !text-[#111D31]'>{item?.inspection_confirmed_by}</label>
                    <span className='text-[10px] text-[#6A6C70]'>{item?.inspection_confirmed_date ? dayjs(item?.inspection_confirmed_date)?.format("DD MMM YYYY") : ""}</span>
                </div>,
                inspection_completed_by: (item) => <div className='py-2.5 flex flex-col gap-0.5'>
                    <label className='mb-0 !text-xs !font-semibold !text-[#111D31]'>{item?.inspection_completed_by}</label>
                    <span className='text-[10px] text-[#6A6C70]'>{item?.inspection_completion_date ? dayjs(item?.inspection_completion_date)?.format("DD MMM YYYY") : ""}</span>
                </div>
            },
            LR: {
                logistic_no: (item) => <div className='py-2.5 flex flex-col'>
                    <label className='mb-0 !text-xs !font-semibold !text-[#111D31]'>{item?.logistic_no}</label>
                    <GenericStatusComponent size='small' className={'mb-0'} status={item?.lr_status} />
                </div>,
                shipment_advice_code: (item) => <label className='mb-0 !text-xs !font-semibold !text-[#111D31]'>{item?.shipment_advice_code}</label>,
                appointment_date: (item) => <label>{item?.appointment_date ? dayjs(item?.appointment_date, getCurrentDateFormat(item?.appointment_date))?.format("DD MMM YYYY") : ""}</label>,
                t_requested_qty: (item) => <div className='py-2.5 flex flex-col gap-0.5'>
                    <label className='mb-0 !text-[11px] !font-medium !text-[#111D31]'>{item?.t_requested_qty}</label>
                    <span className='text-[10px] text-[#6A6C70]'>No. of Cartons: <span className='font-medium'>{item?.no_of_cartons}</span></span>
                </div>,
            },
            GRC: {
                grc_number: (item) => <div className='py-2.5 flex flex-col gap-0.5'>
                    <label className='mb-0 !text-xs !font-semibold !text-[#111D31]'>{item?.grc_number}</label>
                    <div className='flex items-center gap-1.5'>
                        <GenericStatusComponent size='small' className={'mb-0'} status={item?.grc_status} />
                        <span className='text-[10px] text-[#6A6C70]'>{item?.erp_grc_date ? dayjs(item?.erp_grc_date, getCurrentDateFormat(item?.erp_grc_date))?.format("DD MMM YYYY") : ""}</span>
                    </div>
                </div>,
                sm_grc_number: (item) => <div className='py-2.5 flex flex-col gap-0.5'>
                    <label className='mb-0 !text-xs !font-semibold !text-[#111D31]'>{item?.sm_grc_number}</label>
                    <span className='text-[10px] text-[#6A6C70]'>{item?.sm_grc_date ? dayjs(item?.sm_grc_date, getCurrentDateFormat(item?.sm_grc_date))?.format("DD MMM YYYY") : ""}</span>
                </div>,
                shipment_advice_code: (item) => <div className='py-2.5 flex flex-col gap-0.5'>
                    <label className='mb-0 !text-xs !font-semibold !text-[#111D31]'>{item?.shipment_advice_code}</label>
                    <span className='text-[10px] text-[#6A6C70]'>ASN Qty: {item?.shipment_qty}</span>
                </div>,
                rejected_qty: (item) => <label className='text-xs font-medium !text-[#EE5A24]'>{item?.rejected_qty}</label>,
                gate_entry_no: (item) => <label className='mb-0 !text-xs !font-semibold !text-[#111D31]'>{item?.gate_entry_no}</label>
            },
            PO: {
                order_no: (item) => <div className='py-2.5 flex flex-col gap-0.5'>
                    <label className='mb-0 !text-xs !font-semibold !text-[#111D31]'>{item?.order_no}</label>
                    <span className='text-[10px] text-[#6A6C70]'>{item?.valid_from ? dayjs(item?.valid_from, getCurrentDateFormat(item?.valid_from))?.format("DD MMM YYYY") : ""} - {item?.valid_to ? dayjs(item?.valid_to, getCurrentDateFormat(item?.valid_to))?.format("DD MMM YYYY") : ""}</span>
                </div>,
                status: (item) => <div className='py-2.5'><GenericStatusComponent size='small' className={'mb-0'} status={item?.status} /></div>,
                t_cancelled_qty: (item) => <label className='!text-xs !font-semibold !text-[#EE5A24]'>{item?.t_cancelled_qty}</label>,
            }
        },
        setAllQtyKeys: {
            set_order_qty: "Ordered: ",
            set_requested_qty: "Requested: ",
            set_cancelled_qty: "Cancelled: ",
            set_grc_qty: "GRC: ",
        },
        imageKey: "image_url",
        headerKeys: {
            order_date: "Order Date",
            shipment_date: "Shipment Date",
            poQty: "ASN Quantity",
            poAmount: "ASN Amount",
            asnValidity: "ASN Validity",
            grc_qty: "GRC Qty",
            asn_qty: "ASN Qty",
            order_canceled_qty: "ASN Cancelled",
            pending_qty: "ASN Pending",
            order_number: "PO Number",
            supplier_name: "Vendor Name",
            shipment_requested_on: "Shipment Requested Date",
            shipment_confirm_date: "Shipment Confirm Date",
            invoice_number: "Invoice No.",
            invoice_date: "Invoice Date",
            po_quantity: "ASN Qty",
            invoice_amount: "Invoice Amount",
        },
        docTabData: {
            ITEM: "Item Details",
            BOX: "Box Packing Details",
            INSPECTION: "Inspection",
            LR: "LR",
            GRC: "GRC",
            PO: "PO",
            DISCREPANCIES: "Discrepancies",
            liveComments: "Comments"
        },
        docViewerComment: {
            disabled: false,
            configKeys: {
                "documentId": "document_id",
                "documentNumber": "shipment_advice_code",
                "vendorCode": "supplier_code",
                "documentType": "documentType"
            }
        },
        docViewerHeaderKeys: uType == "ENT" ? {
            MAIN: {
                name: "Main",
                key: "ENT_DOCUMENT_VIEWER_ASN"
            },
            ITEM: {
                name: "Item",
                key: "ENT_DOCUMENT_VIEWER_ASN_ITEM"
            },
            BOX: {
                name: "BOX",
                key: "ENT_DOCUMENT_VIEWER_PO_BOX"
            },
            INSPECTION: {
                name: "INSPECTION",
                key: "ENT_DOCUMENT_VIEWER_PO_INSPECTION"
            },
            LR: {
                name: "LR",
                key: "ENT_DOCUMENT_VIEWER_PO_LR"
            },
            GRC: {
                name: "GRC",
                key: "ENT_DOCUMENT_VIEWER_PO_GRC"
            },
            PO: {
                name: "Indent",
                key: "ENT_DOCUMENT_VIEWER_PO_PO"
            },
            DISCREPANCIES: {
                name: "Discrepancies",
                key: "ENT_DOCUMENT_VIEWER_ASN_DISCREPANCY"
            }
        } : {
            MAIN: {
                name: "Main",
                key: "VENDOR_DOCUMENT_VIEWER_ASN"
            },
            ITEM: {
                name: "Item",
                key: "VENDOR_DOCUMENT_VIEWER_ASN_ITEM"
            },
            BOX: {
                name: "BOX",
                key: "VENDOR_DOCUMENT_VIEWER_PO_BOX"
            },
            INSPECTION: {
                name: "INSPECTION",
                key: "VENDOR_DOCUMENT_VIEWER_PO_INSPECTION"
            },
            ASN: {
                name: "ASN",
                key: "VENDOR_DOCUMENT_VIEWER_PO_ASN"
            },
            LR: {
                name: "LR",
                key: "VENDOR_DOCUMENT_VIEWER_PO_LR"
            },
            GRC: {
                name: "GRC",
                key: "VENDOR_DOCUMENT_VIEWER_PO_GRC"
            },
            PI: {
                name: "Indent",
                key: "VENDOR_DOCUMENT_VIEWER_PO_PI"
            },
            DISCREPANCIES: {
                name: "Discrepancies",
                key: "VENDOR_DOCUMENT_VIEWER_ASN_DISCREPANCY"
            }
        },
        headerKeyIcons: {
            order_number: <File />,
            supplier_name: <User />,
            shipment_requested_on: <Icons.ShipmentRequestDate height={14} />,
            shipment_confirm_date: <CalendarCheck2 height={14} />,
            invoice_number: <Icons.InvoiceNo height={14} />,
            invoice_date: <Icons.InvoiceDate height={14} />
        },
        headerConfig: {
            section1: {
                timeKey: "createdOnDate",
                documentIcon: <span><FileText strokeWidth={1.75} /></span>,
                statusKeys: ["is_set", "status", "view_remarks"],
                headerKey: "shipment_advice_code",
                remarksKey: "remark",
                allDetails: {},
            },
            section2: {
                detailsKeys: ["order_number", "supplier_name", "shipment_requested_on", "shipment_confirm_date", "invoice_number", "invoice_date"]
            },
            section3: {
                exportToExcel: true,
                downloadDocuments: true,
            },
            section4: {
                totalQty: "asn_qty",
                amountKey: "invoice_amount",
                remainingQtyKey: ["grc_qty", "order_canceled_qty", "pending_qty"],
            },
            section5: {
                fulfillMentPercentage: "GRC Accuracy",
                discrepancy: "Discrepancy Rate",
            }
        },
        // modifyMainLevelData: (data) => {
        // 	return data?.resource?.[0] || {}
        // },
        columnConfig: {
            "createdOnDate": (config = {}) => {
                let { data, } = config || {};
                return data?.["shipment_requested_on"] ? dayjs(data?.["shipment_requested_on"] || "")?.format("MMM DD YYYY HH:mm") : "";
            },
            "shipment_requested_on": (config = {}) => {
                let { data, } = config || {};
                return data?.["shipment_requested_on"] ? dayjs(data?.["shipment_requested_on"] || "")?.format("MMM DD YYYY HH:mm") : "-";
            },
            "shipment_confirm_date": (config = {}) => {
                let { data, } = config || {};
                return data?.["shipment_confirm_date"] ? dayjs(data?.["shipment_confirm_date"] || "")?.format("MMM DD YYYY HH:mm") : "-";
            },
            "invoice_date": (config = {}) => {
                let { data, } = config || {};
                return data?.["invoice_date"] ? dayjs(data?.["invoice_date"] || "")?.format("MMM DD YYYY HH:mm") : "-";
            },
            "is_set": (config = {}) => {
                let item = { ...config?.data, poType: config?.data?.is_set == "true" ? "SET" : "NON-SET", status: config?.data?.po_status }
                return (
                    <>
                        <div className="poStatus">
                            {item.status == "PARTIAL" ? <span className="partialTable partiaTag labelEvent">P</span> : null}
                            {item.poType != "SET" ? <span className="partialTable setTag nonSetTag labelEvent">N</span> : <span className="partialTable setTag labelEvent">S</span>}
                            <div className="poStatusToolTip">
                                {item.status == "PARTIAL" ? <div className="each-tag"><span className="partialTable partiaTag labelEvent">P</span><LanguageTranslator tag="label">Partial</LanguageTranslator></div> : ""}
                                {item.poType != "SET" ? <div className="each-tag"><span className="partialTable setTag nonSetTag labelEvent">N</span><LanguageTranslator tag="label">Non-set</LanguageTranslator></div> : <div className="each-tag"><span className="partialTable setTag labelEvent">S</span><LanguageTranslator tag="label">Set</LanguageTranslator></div>}
                            </div>
                        </div>
                    </>
                );
            },
            "status": (data) => <div className='flex'><GenericStatusComponent className={'mb-0'} status={data?.shipment_status} /><GenericStatusComponent className={'mb-0 ml-2'} status={data?.qc_status} /></div>,
            "asnValidity": (config = {}) => {
                let { data } = config || {};
                return `${data?.validFromDate ? dayjs(data?.validFromDate)?.format("DD MMM YYYY") : ""} - ${data?.validToDate ? dayjs(data?.validToDate)?.format("DD MMM YYYY") : ""}`;
            }
        },
    }

}